import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Building2, Store as ShopIcon, BadgeCheck, MoreVertical, Trash2 } from "lucide-react";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { PrimaryButton, SecondaryButton } from "./Buttons";
import { toast } from "sonner";

/**
 * Business row card — used on Businesses screen + as embedded row in other lists.
 * A "business" is a unified concept — it can send or receive both enquiries and orders.
 *   business = { id, name, phone, created_at, on_app? }
 *
 * #3 (iter5) — Connected businesses expose a "Remove business" action in an
 * overflow menu. Confirmation dialog before the DELETE call. The backend
 * removes both sides of the link and clears any pending requests.
 */
export function BusinessCard({ business, to, testid, onRemoved }) {
  const initial = (business.name || "?").charAt(0).toUpperCase();
  const dateStr = business.created_at
    ? new Date(business.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "";
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const doRemove = async () => {
    setBusy(true);
    try {
      await apiClient.delete(`/suppliers/${business.raw_id || business.id}`);
      toast.success(business.on_app ? `${business.name} disconnected — both businesses removed` : `${business.name} removed`);
      setConfirmOpen(false);
      onRemoved && onRemoved(business);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove");
    } finally {
      setBusy(false);
    }
  };

  const Body = (
    <div
      data-testid={testid || `business-card-${business.id}`}
      className="relative flex items-center gap-3 bg-white border border-slate-100 rounded-2xl p-3.5 hover:shadow-md hover:border-slate-200 transition-all duration-200 active:scale-[0.99]"
    >
      <div className="relative w-11 h-11 rounded-2xl grid place-items-center shrink-0 font-bold bg-emerald-100 text-emerald-600">
        {business.avatar_letter ?? initial}
        {business.on_app && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <div className="font-semibold text-slate-900 truncate">{business.name}</div>
          {business.on_app && (
            <span
              data-testid={`business-on-app-${business.id}`}
              className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full shrink-0"
            >
              <BadgeCheck className="w-3 h-3" /> On App
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          {business.phone || "—"}
          {dateStr && <span className="text-slate-400"> · Connected {dateStr}</span>}
        </div>
      </div>
      {onRemoved ? (
        <button
          type="button"
          data-testid={`business-menu-${business.raw_id || business.id}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen((v) => !v); }}
          className="w-9 h-9 rounded-lg hover:bg-slate-100 grid place-items-center shrink-0 text-slate-500"
          aria-label="More actions"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
      ) : (
        <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
      )}
      {menuOpen && (
        <div
          data-testid={`business-menu-panel-${business.raw_id || business.id}`}
          className="absolute right-3 top-14 z-20 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[180px]"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          <button
            type="button"
            data-testid={`business-remove-${business.raw_id || business.id}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(false); setConfirmOpen(true); }}
            className="w-full flex items-center gap-2 text-left px-3 h-10 text-sm font-semibold text-red-600 hover:bg-red-50 transition"
          >
            <Trash2 className="w-4 h-4" /> Remove business
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {to ? <Link to={to}>{Body}</Link> : Body}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="rounded-3xl max-w-sm" data-testid={`business-remove-dialog-${business.raw_id || business.id}`}>
          <DialogHeader>
            <DialogTitle>Remove connected business?</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <b>{business.name}</b>?
              {business.on_app
                ? " Both businesses will be disconnected. Past orders, enquiries, quotations and tasks stay intact — but you'll need to reconnect before sending new ones."
                : " This business will be removed from your list. Past history stays intact."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <SecondaryButton onClick={() => setConfirmOpen(false)} data-testid={`business-remove-cancel-${business.raw_id || business.id}`}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={doRemove}
              loading={busy}
              data-testid={`business-remove-confirm-${business.raw_id || business.id}`}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="w-4 h-4 mr-1 inline" /> Remove
            </PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

BusinessCard.ShopIcon = ShopIcon;
BusinessCard.Building = Building2;
